import { useUpdateLabMutation } from "../service/queries";
import { showNotification } from "@libs/notistack/notificationStack";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import type { Lab, UpdateLabRequest } from "../types";
import type { Equipment } from "../../EquipmentsTab/types/types";
import { useTranslation } from "react-i18next";

interface SelectedEquipment {
	equipment: Equipment;
	count: number;
}

interface UseUpdateLabProps {
	onSuccess?: () => void;
	onError?: (error: string) => void;
}

export const useUpdateLab = ({ onSuccess, onError }: UseUpdateLabProps = {}) => {
	const updateLabMutation = useUpdateLabMutation();
	const { t } = useTranslation();
	const updateLab = async (
		originalLab: Lab,
		newName: string,
		newDescription: string,
		newEquipments: SelectedEquipment[],
	) => {
		try {
			// Prepare the update request
			const updateRequest: UpdateLabRequest = {};

			// Check if name has changed
			if (newName !== originalLab.name) {
				updateRequest.name = newName;
			}

			const normalizedDescription = newDescription.trim();
			const originalDescription = originalLab.description?.trim() || "";
			if (normalizedDescription !== originalDescription) {
				updateRequest.description = normalizedDescription || null;
			}

			// Compare equipment changes
			const originalEquipmentMap = new Map((originalLab.labEquipments || []).map(le => [le.equipment.uuid, le]));
			const newEquipmentMap = new Map(newEquipments.map(se => [se.equipment.uuid, se]));

			// Find equipment to add (new equipment not in original)
			const addEquipments: Array<{ equipmentId: string; requiredCount: number }> = [];
			for (const [equipmentUuid, selectedEquipment] of newEquipmentMap) {
				if (!originalEquipmentMap.has(equipmentUuid)) {
					addEquipments.push({
						equipmentId: equipmentUuid,
						requiredCount: selectedEquipment.count,
					});
				}
			}

			// Find equipment to remove (original equipment not in new)
			const removeLabEquipments: string[] = [];
			for (const [equipmentUuid, labEquipment] of originalEquipmentMap) {
				if (!newEquipmentMap.has(equipmentUuid)) {
					removeLabEquipments.push(labEquipment.uuid);
				}
			}

			// Find equipment with changed counts (existing equipment with different count)
			// For count changes, we need to remove the old equipment and add it back with new count
			for (const [equipmentUuid, selectedEquipment] of newEquipmentMap) {
				if (originalEquipmentMap.has(equipmentUuid)) {
					const originalLabEquipment = originalEquipmentMap.get(equipmentUuid)!;
					if (selectedEquipment.count !== originalLabEquipment.requiredCount) {
						// Remove the existing equipment
						removeLabEquipments.push(originalLabEquipment.uuid);
						// Add it back with the new count
						addEquipments.push({
							equipmentId: equipmentUuid,
							requiredCount: selectedEquipment.count,
						});
					}
				}
			}

			// Only add to request if there are changes
			if (addEquipments.length > 0) {
				updateRequest.addEquipments = addEquipments;
			}

			if (removeLabEquipments.length > 0) {
				updateRequest.removeLabEquipments = removeLabEquipments;
			}

			// If no changes, show message and return
			if (Object.keys(updateRequest).length === 0) {
				showNotification({
					message: t("feature.courseBuilder.labs.errors.noChangesDetected"),
					severity: SNACKBAR_SEVERITY.INFO,
				});
				return;
			}

			// Call the update mutation
			await updateLabMutation.mutateAsync({
				labUuid: originalLab.uuid,
				labData: updateRequest,
			});

			onSuccess?.();
		} catch (error) {
			console.error("Error updating lab:", error);
			const errorMessage = error instanceof Error ? error.message : "Failed to update lab";
			onError?.(errorMessage);
		}
	};

	return {
		updateLab,
		isUpdating: updateLabMutation.isPending,
		error: updateLabMutation.error,
	};
};
